import {ElementRef, Injectable} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash-es';
import {animationFrameScheduler, defer, fromEvent, NEVER, Observable, Subscription} from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    share,
    startWith,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {cancellableDebounceTime} from '../../common/rxjs/cancellable-debounce-time';
import {DynamicComponentRef} from '../dynamic-component';
import {ModalService} from '../modal';
import {normalizeTranslationArgs} from '../translate/translation-args';
import {TooltipBalloonComponent} from './tooltip-balloon';

interface MousePosition {
    x: number;
    y: number;
}

const TOOLTIP_SHOW_DELAY = 250;
const TOOLTIP_HIDE_DELAY = 50;

@Injectable()
export class TooltipService {

    private tooltipBalloonComponentRef: DynamicComponentRef<TooltipBalloonComponent>;

    constructor(
        private elementRef: ElementRef,
        private modalService: ModalService,
        private translationService: L10nTranslationService
    ) {}

    public init(
        referenceElementRef: ElementRef,
        isEnabled$: Observable<boolean>,
        tooltipContent$: Observable<string>
    ): Observable<unknown> {
        return isEnabled$.pipe(
            switchMap(isEnabled => isEnabled
                ? this.listenToMouseEvents(
                    this.elementRef,
                    tooltipContent$
                )
                : NEVER
            )
        );
    }

    public getL10nTooltipContentStream(textToTranslate: string, translationArgs?: any): Observable<string> {
        const args = normalizeTranslationArgs(translationArgs);
        const getContent = () => _.isString(textToTranslate)
            ? this.translationService.translate(textToTranslate, args)
            : String(textToTranslate || '');

        return this.translationService.onChange().pipe(
            startWith(undefined),
            map(getContent)
        );
    }

    private listenToMouseEvents(
        referenceElementRef: ElementRef,
        tooltipContent$: Observable<string>
    ): Observable<any> {
        let mouseTracking: Subscription;
        let mousePosition: MousePosition;

        const element: HTMLElement = this.elementRef.nativeElement;
        const storeMousePos = tap((event: MouseEvent) => mousePosition = {x: event.pageX, y: event.pageY});

        const mouseEnter$ = fromEvent<MouseEvent>(element, 'mouseenter').pipe(
            storeMousePos,
            share()
        );
        const mouseLeave$ = fromEvent<MouseEvent>(element, 'mouseleave').pipe(
            share()
        );
        const trackMouse$ = fromEvent<MouseEvent>(element, 'mousemove').pipe(
            storeMousePos
        );

        const lazyMouseEnter$ = mouseEnter$.pipe(
            filter(() => !this.tooltipBalloonComponentRef),
            tap(() => mouseTracking = mouseTracking || trackMouse$.subscribe()),
            cancellableDebounceTime(TOOLTIP_SHOW_DELAY, mouseLeave$, animationFrameScheduler)
        );

        const lazyMouseLeave$ = mouseLeave$.pipe(
            cancellableDebounceTime(TOOLTIP_HIDE_DELAY, mouseEnter$, animationFrameScheduler)
        );

        const showTooltip$ = defer(() =>
            this.createTooltipBalloon(
                referenceElementRef,
                tooltipContent$,
                mousePosition
            )
        ).pipe(
            tap(tooltipRef => this.tooltipBalloonComponentRef = tooltipRef),
            takeUntil(lazyMouseLeave$),
            finalize(() => {
                mouseTracking.unsubscribe();
                mouseTracking = null;

                this.tooltipBalloonComponentRef = null;
            })
        );

        return lazyMouseEnter$.pipe(
            switchMapTo(showTooltip$)
        );
    }

    private createTooltipBalloon(
        referenceElementRef: ElementRef,
        tooltipContent$: Observable<string>,
        mousePos: MousePosition
    ): Observable<DynamicComponentRef<TooltipBalloonComponent>> {
        return tooltipContent$.pipe(
            take(1),
            switchMap(firstTooltipContent =>
                new Observable<DynamicComponentRef<TooltipBalloonComponent>>(subscriber => {
                    const tooltipRef = this.modalService.open(TooltipBalloonComponent, {
                        configure: {
                            referenceElementRef,
                            tooltip:       firstTooltipContent,
                            mousePosition: mousePos
                        }
                    });

                    /**
                     * Workaround for the case when parent has {@link ChangeDetectionStrategy.OnPush}.
                     */
                    tooltipRef.componentRef.changeDetectorRef.detectChanges();

                    const subscription = tooltipContent$.pipe(
                        startWith(firstTooltipContent),
                        distinctUntilChanged(),
                        delay(0), // Allow the current change detection cycle to finish.
                        tap(nextTooltipContent => tooltipRef.instance.tooltip = nextTooltipContent)
                    ).subscribe();

                    subscription.add(() => tooltipRef.destroy());
                    subscriber.next(tooltipRef);

                    return subscription;
                })
            )
        );
    }

}
